import { useEffect } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router'
import Grid from '@mui/material/Grid'
import { AdCreativeWithUrls, AdPod, AdPodInit, AdPodUpdate, Group, Role } from 'common/api/v1/types'
import RHF, { FormProps } from '../../../common/Form/RHF'
import { AppDispatch, GlobalState, useRoutes } from '../../../../store'
import Wrapper from '../../../common/Wrapper'
import Pendable from '../../../common/Pendable'
import { formTransform, useConfirmationDialog, useUser } from '../../../../utils'
import { deleteAdCreative } from '../../../../redux/actions/adCreativeActions'
import { ButtonsPane, Form, Paper, SafeRouting, TextInput } from '../../../common/Form'
import { equals } from 'common/util'
import { clearAdPod, createAdPod, getAdPod, updateAdPod } from '../../../../redux/actions/adPodActions'
import TransferList from './TransferList'
import { useFieldArray } from 'react-hook-form'
import { AdCreativeRHF } from '../utils'

type FormState = AdPodUpdate &
  AdPodInit & {
    adPod?: AdPod
  }

const getInitialState = (adPod?: AdPod): FormState => ({
  id: adPod?.id || '',
  name: adPod?.name || '',
  outputIds: adPod?.outputIds || [],
  creatives: adPod?.creatives || [],
  adPod: adPod,
})

const isEditing = (values: FormState) => values?.adPod?.id !== undefined

const AdPodForm = ({ getValues, setError, setFocus, formState }: FormProps<FormState>) => {
  const values = getValues()
  const routes = useRoutes()
  const navigate = useNavigate()
  const dispatch = useDispatch<AppDispatch>()
  const setConfirmation = useConfirmationDialog()

  const { formErrors, saving } = useSelector(
    ({ groupsReducer }: GlobalState) => ({
      formErrors: groupsReducer.formErrors,
      saving: groupsReducer.saving,
    }),
    shallowEqual,
  )

  const {
    fields: selectedAdCreatives,
    replace,
    append,
  } = useFieldArray({
    name: 'creatives',
    keyName: 'fieldId',
  })

  useEffect(() => {
    if (Array.isArray(selectedAdCreatives) && selectedAdCreatives.length > 0) {
      const newSelectedAdCreatives = selectedAdCreatives.map((item, index) => ({
        ...item,
        locId: `${index}`,
      }))
      replace(newSelectedAdCreatives)
    }
  }, [selectedAdCreatives.length])

  const user = useUser()

  useEffect(() => {
    if (Array.isArray(formErrors)) {
      formErrors.forEach((item) => {
        setError(item.name as keyof FormState, { type: 'manual', message: item.reason })
      })
      if (formErrors[0]) setFocus(formErrors[0].name as keyof FormState)
    }
  }, [formErrors, setError, setFocus])

  if (user.role !== Role.super && (user.role !== Role.admin || user.group !== (values as Group).id)) {
    navigate(-1)
    return null
  }

  return (
    <Grid container>
      <Grid size={{ xs: 12 }}>
        <SafeRouting formState={formState} />
        <Form id="ad-creative-form" noValidate>
          <Paper sx={{ p: 2 }}>
            <TextInput name="name" label="Name" required autoFocus />
          </Paper>

          <Paper title="Creatives" className="outlined">
            <TransferList
              adCreatives={selectedAdCreatives as AdCreativeRHF[]}
              onAdd={(adCreatives: AdCreativeWithUrls[]) => {
                append(adCreatives)
              }}
              onRemove={(adCreatives: AdCreativeRHF[]) => {
                const newAdCreatives = (selectedAdCreatives as AdCreativeRHF[]).filter(
                  (item) => !adCreatives.find((i) => i.locId === item.locId),
                )
                replace(newAdCreatives)
              }}
              onToggleFiller={(adCreatives: AdCreativeWithUrls[]) => {
                const anyNotFiller = adCreatives.some((c) => !c.filler)
                const newAdCreatives = (selectedAdCreatives as AdCreativeRHF[]).map((item) => ({
                  ...item,
                  filler: (adCreatives as AdCreativeRHF[]).find((ad) => ad.locId == item.locId)
                    ? anyNotFiller
                    : item.filler,
                }))
                replace(newAdCreatives)
              }}
              onReorder={(item: AdCreativeRHF, from: number, to: number) => {
                const newAdCreatives = [...(selectedAdCreatives as AdCreativeRHF[])]
                newAdCreatives.splice(from, 1)
                newAdCreatives.splice(to, 0, item as AdCreativeRHF)
                newAdCreatives.forEach((creative, index) => {
                  creative.locId = `${index}`
                })
                replace(newAdCreatives)
              }}
            />
          </Paper>

          <ButtonsPane
            main={{
              Cancel: {
                onClick: () => navigate(routes.adCreatives()),
              },
              Save: {
                savingState: !!saving,
                primary: true,
                type: 'submit',
              },
            }}
            secondary={
              user.role === Role.super
                ? {
                    Delete: {
                      onClick: () =>
                        setConfirmation(
                          () => {
                            dispatch(deleteAdCreative(values.id))
                            navigate(routes.adCreatives())
                          },
                          'Are you sure you want to delete this Ad Pod?',
                          values.name,
                        ),
                    },
                  }
                : undefined
            }
          />
        </Form>
      </Grid>
    </Grid>
  )
}

export const EditAdPod = () => {
  const { id } = useParams()
  const dispatch = useDispatch<AppDispatch>()
  useEffect(() => {
    id && dispatch(getAdPod(id))
    return () => {
      dispatch(clearAdPod())
    }
  }, [])

  const selectedAdPod = useSelector(({ adPodReducer }: GlobalState) => adPodReducer.selectedAdPod, equals)

  return (
    <Wrapper name={['Ad Insertion', 'Ad Pods', id ? selectedAdPod?.name : 'New']}>
      <Pendable pending={!!id && !selectedAdPod}>
        <RHF
          onSubmit={(values) => {
            const { adPod, ...updateOrCreate } = values
            if (isEditing(values)) {
              dispatch(updateAdPod(formTransform(updateOrCreate)))
            } else {
              dispatch(createAdPod(formTransform(updateOrCreate)))
            }
          }}
          defaultValues={getInitialState(selectedAdPod)}
          component={AdPodForm}
        />
      </Pendable>
    </Wrapper>
  )
}
